export default function ManualFeeTransactionsModal({
  transactions = [],
  loading,
  error,
  onClose,
  inr,
  formatDate,
  formatDateTime,
  onRetry,
}) {
  // ------------------------------------------------------------
  // Student Information
  // API now returns normalized student_info
  // ------------------------------------------------------------
  const getStudentName = (item) => item?.student_info?.name || "-";

  const getRegistrationNo = (item) =>
    item?.student_info?.registration_no || null;

  const getStudentType = (item) => item?.student_info?.type || null;

  // ------------------------------------------------------------
  // Manual Modules
  // ------------------------------------------------------------
  const getManualModules = (item) => {
    if (Array.isArray(item?.manual_modules)) {
      return item.manual_modules;
    }

    try {
      const modules =
        typeof item?.module === "string"
          ? JSON.parse(item.module)
          : item?.module || [];

      if (!Array.isArray(modules)) {
        return [];
      }

      return modules.filter((module) => module?.manual === true);
    } catch (error) {
      return [];
    }
  };

  // ------------------------------------------------------------
  // Total Amount
  // ------------------------------------------------------------
  const totalAmount = transactions.reduce(
    (sum, item) => sum + Number(item?.amount || 0),
    0,
  );

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        role="document"
        style={{
          maxWidth: "1250px",
        }}
      >
        <div
          className="modal-content border-0 shadow-lg rounded-4 overflow-hidden"
          style={{
            maxHeight: "90vh",
          }}
        >
          {/* ================================================== */}
          {/* HEADER */}
          {/* ================================================== */}

          <div className="modal-header border-bottom px-4 py-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  background: "#8b5cf615",
                  color: "#8b5cf6",
                  flexShrink: 0,
                }}
              >
                <i className="ri-wallet-3-line fs-4" />
              </div>

              <div>
                <h5 className="modal-title fw-bold mb-0">
                  Manual Fee Transactions
                </h5>

                <small className="text-muted">
                  Fee deposits entered manually
                </small>
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          {/* ================================================== */}
          {/* BODY */}
          {/* ================================================== */}

          <div className="modal-body p-0">
            {/* ================================================= */}
            {/* LOADING */}
            {/* ================================================= */}

            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                />

                <h6 className="fw-semibold">Loading manual transactions...</h6>

                <p className="text-muted mb-0">
                  Please wait while we fetch the fee deposits.
                </p>
              </div>
            ) : error ? (
              /* ================================================= */
              /* ERROR */
              /* ================================================= */

              <div className="text-center py-5 px-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{
                    width: 64,
                    height: 64,
                    background: "#fee2e2",
                    color: "#dc2626",
                  }}
                >
                  <i className="ri-error-warning-line fs-2" />
                </div>

                <h6 className="fw-bold">Unable to load transactions</h6>

                <p className="text-muted mb-3">{error}</p>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onRetry}
                >
                  <i className="ri-refresh-line me-1" />
                  Retry
                </button>
              </div>
            ) : transactions.length === 0 ? (
              /* ================================================= */
              /* EMPTY */
              /* ================================================= */

              <div className="text-center py-5 px-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{
                    width: 70,
                    height: 70,
                    background: "#f1f5f9",
                    color: "#64748b",
                  }}
                >
                  <i className="ri-wallet-3-line fs-2" />
                </div>

                <h6 className="fw-bold mb-1">No Manual Fee Transactions</h6>

                <p className="text-muted mb-0">
                  No manually deposited fee transactions were found.
                </p>
              </div>
            ) : (
              /* ================================================= */
              /* DATA */
              /* ================================================= */

              <>
                {/* ============================================== */}
                {/* SUMMARY */}
                {/* ============================================== */}

                <div className="p-3 border-bottom bg-light">
                  <div className="row g-3">
                    {/* Total Transactions */}
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm rounded-3 h-100">
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted d-block">
                                Total Transactions
                              </small>

                              <h5 className="fw-bold mb-0 mt-1">
                                {transactions.length.toLocaleString("en-IN")}
                              </h5>
                            </div>

                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 40,
                                height: 40,
                                background: "#e0e7ff",
                                color: "#4f46e5",
                              }}
                            >
                              <i className="ri-file-list-3-line fs-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Total Amount */}
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm rounded-3 h-100">
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted d-block">
                                Total Amount
                              </small>

                              <h5 className="fw-bold text-success mb-0 mt-1">
                                {inr(totalAmount)}
                              </h5>
                            </div>

                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 40,
                                height: 40,
                                background: "#dcfce7",
                                color: "#16a34a",
                              }}
                            >
                              <i className="ri-money-rupee-circle-line fs-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Manual Entries */}
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm rounded-3 h-100">
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted d-block">
                                Manual Entries
                              </small>

                              <h5 className="fw-bold mb-0 mt-1">
                                {transactions.length.toLocaleString("en-IN")}
                              </h5>
                            </div>

                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 40,
                                height: 40,
                                background: "#f3e8ff",
                                color: "#9333ea",
                              }}
                            >
                              <i className="ri-edit-box-line fs-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ============================================== */}
                {/* TABLE */}
                {/* ============================================== */}

                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="px-4" style={{ minWidth: 55 }}>
                          #
                        </th>

                        <th style={{ minWidth: 220 }}>
                          <i className="ri-user-3-line me-1" />
                          Student
                        </th>

                        <th style={{ minWidth: 130 }}>
                          <i className="ri-file-list-2-line me-1" />
                          Receipt
                        </th>

                        <th style={{ minWidth: 210 }}>
                          <i className="ri-wallet-3-line me-1" />
                          Fee
                        </th>

                        <th style={{ minWidth: 120 }}>
                          <i className="ri-money-rupee-circle-line me-1" />
                          Amount
                        </th>

                        <th style={{ minWidth: 140 }}>
                          <i className="ri-bank-card-line me-1" />
                          Payment
                        </th>

                        <th style={{ minWidth: 150 }}>
                          <i className="ri-hashtag me-1" />
                          Transaction No.
                        </th>

                        <th style={{ minWidth: 130 }}>
                          <i className="ri-calendar-line me-1" />
                          Payment Date
                        </th>

                        <th style={{ minWidth: 180 }}>
                          <i className="ri-time-line me-1" />
                          Created
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {transactions.map((item, index) => {
                        const modules = getManualModules(item);

                        const studentName = getStudentName(item);

                        const registrationNo = getRegistrationNo(item);

                        const studentType = getStudentType(item);

                        return (
                          <tr key={item?.id ?? `manual-${index}`}>
                            {/* # */}
                            <td className="px-4">
                              <span className="text-muted fw-medium">
                                {index + 1}
                              </span>
                            </td>

                            {/* STUDENT */}
                            <td>
                              <div className="d-flex align-items-start gap-2">
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 34,
                                    height: 34,
                                    background: "#f1f5f9",
                                    color: "#64748b",
                                    flexShrink: 0,
                                  }}
                                >
                                  <i className="ri-user-line" />
                                </div>

                                <div
                                  style={{
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    className="fw-semibold text-truncate"
                                    style={{
                                      maxWidth: 170,
                                    }}
                                    title={studentName}
                                  >
                                    {studentName}
                                  </div>

                                  <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                                    {registrationNo && (
                                      <small className="text-muted">
                                        <i className="ri-hashtag me-1" />

                                        {registrationNo}
                                      </small>
                                    )}

                                    {studentType && (
                                      <span
                                        className="badge bg-light text-secondary border"
                                        style={{
                                          fontSize: "10px",
                                        }}
                                      >
                                        {studentType}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* RECEIPT */}
                            <td>
                              <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2">
                                {item?.receipt_no || "-"}
                              </span>
                            </td>

                            {/* FEE */}
                            <td>
                              {modules.length ? (
                                modules.map((module, moduleIndex) => (
                                  <div
                                    key={moduleIndex}
                                    className="d-flex align-items-center justify-content-between gap-2 mb-1"
                                  >
                                    <span
                                      className="badge bg-light text-dark border"
                                      style={{
                                        fontWeight: 500,
                                      }}
                                    >
                                      {module?.type || "Manual Fee"}
                                    </span>

                                    <strong className="text-success">
                                      {inr(module?.amount)}
                                    </strong>
                                  </div>
                                ))
                              ) : (
                                <span className="text-muted">Manual Fee</span>
                              )}
                            </td>

                            {/* AMOUNT */}
                            <td>
                              <strong className="text-success">
                                {inr(item?.amount)}
                              </strong>
                            </td>

                            {/* PAYMENT METHOD */}
                            <td>
                              <span className="badge bg-info-subtle text-info rounded-pill">
                                <i className="ri-bank-card-line me-1" />

                                {item?.payment_method || "-"}
                              </span>
                            </td>

                            {/* TRANSACTION NUMBER */}
                            <td>
                              {item?.transaction_no ? (
                                <span className="font-monospace text-muted">
                                  {item.transaction_no}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            {/* PAYMENT DATE */}
                            <td>
                              <span className="fw-medium">
                                {formatDate(item?.payment_date)}
                              </span>
                            </td>

                            {/* CREATED */}
                            <td>
                              <small className="text-muted d-block">
                                {formatDateTime(item?.created_at)}
                              </small>

                              {item?.created_by?.name && (
                                <small className="text-muted d-block mt-1">
                                  <i className="ri-user-line me-1" />

                                  {item.created_by.name}
                                </small>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* ================================================== */}
          {/* FOOTER */}
          {/* ================================================== */}

          <div className="modal-footer border-top">
            <button
              type="button"
              className="btn btn-light border"
              onClick={onClose}
            >
              <i className="ri-close-line me-1" />
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
